// src/pages/settings/invoices/InvoiceSettingsAutomationPage.tsx
import { Box, Card, Field, Flex, HStack, SimpleGrid, VStack } from '@chakra-ui/react'
import { useMemo } from 'react'
import { HiCheck, HiX } from 'react-icons/hi'
import { LuMail, LuMailPlus } from 'react-icons/lu'

import {
  SwitchRoot,
  SwitchHiddenInput,
  SwitchControl,
  SwitchThumb,
  SwitchThumbIndicator,
} from '@chakra-ui/react'

import { useInvoiceSettingsCtx } from './InvoiceSettingsShell'
import { MissingVatRateHint } from './MissingVatRateHint'
import { SettingsActionBar } from '@/pages/settings/SettingsActionBar' // <- adjust path if needed
import { SettingsInput } from '../SettingsControls'
import { FormLayout } from '@/components/layout/FormLayout'

type AutomationMode = 'CREATE_AND_SEND' | 'CREATE_ONLY'

function requiredMark() {
  return <Field.RequiredIndicator />
}

export function InvoiceSettingsAutomationPage() {
  const {
    saving,
    serverErrors,
    clearServerError,
    autoFormik,
    hasAutoChanged,
    saveAuto,
    vatRateMissing,
  } = useInvoiceSettingsCtx()

  const enabled = !!autoFormik.values.autoGenerationEnabled
  const sendAfterAutoGeneration = !!autoFormik.values.sendAfterAutoGeneration

  const modeCards = useMemo(
    () =>
      [
        {
          key: 'CREATE_AND_SEND' as const,
          title: 'Rechnungen erstellen & versenden',
          description:
            'Das System erstellt die Rechnungen automatisch und sendet sie zur Prüfung an Union Investment. Nach der Freigabe werden sie an die Rechnungsabteilung weitergeleitet.',
          icon: <LuMail />,
        },
        {
          key: 'CREATE_ONLY' as const,
          title: 'Rechnungen erstellen',
          description: 'Das System erstellt die Rechnungen automatisch, versendet sie aber nicht.',
          icon: <LuMailPlus />,
        },
      ] satisfies Array<{
        key: AutomationMode
        title: string
        description: string
        icon: React.ReactNode
      }>,
    []
  )

  return (
    <FormLayout.Root data-testid="invoice-settings-automation">
      {/* Header */}
      <FormLayout.Section>
        <FormLayout.Title>Rechnungsautomatik</FormLayout.Title>

        <FormLayout.Description>
          Das System kann Ihre Rechnungen zu einem von Ihnen festgelegten Zeitpunkt jedes Monats
          automatisch erstellen. Die erstellten Rechnungen werden zunächst zur Prüfung an Union
          Investment gesendet. Nach erfolgter Freigabe werden sie automatisch an die
          Rechnungsabteilung weitergeleitet.
        </FormLayout.Description>

        <FormLayout.Description>
          Rechnungen können jederzeit auch manuell erstellt werden – unabhängig von der Automatik.
          Manuell abgerechnete Aktivitäten fließen nicht mehr in die nächste automatische Rechnung
          ein.
        </FormLayout.Description>
      </FormLayout.Section>

      {vatRateMissing && <MissingVatRateHint />}

      {/* Switch */}
      <Box>
        <HStack gap={4} align="center">
          <SwitchRoot
            checked={enabled}
            onCheckedChange={(detail: { checked: boolean }) => {
              const isEnabled = detail.checked
              autoFormik.setFieldValue('autoGenerationEnabled', isEnabled)
              clearServerError('autoGenerationEnabled')

              if (isEnabled) {
                const pol = autoFormik.values.activityInvoiceResetPolicy
                if (!['YEARLY', 'CONTINUOUS'].includes(pol as any)) {
                  autoFormik.setFieldValue('activityInvoiceResetPolicy', 'YEARLY')
                }
                clearServerError('activityInvoiceResetPolicy')
                autoFormik.setFieldError('activityInvoiceResetPolicy', undefined)
              } else {
                clearServerError('activityInvoiceResetPolicy')
                autoFormik.setFieldError('activityInvoiceResetPolicy', undefined)
              }
            }}
            disabled={saving}
            colorPalette="green"
          >
            <SwitchHiddenInput />
            <SwitchControl>
              <SwitchThumb>
                <SwitchThumbIndicator fallback={<HiX />}>
                  <HiCheck />
                </SwitchThumbIndicator>
              </SwitchThumb>
            </SwitchControl>
          </SwitchRoot>

          <FormLayout.Title>Rechnungsautomatik konfigurieren</FormLayout.Title>
        </HStack>

        {(serverErrors.autoGenerationEnabled || autoFormik.errors.autoGenerationEnabled) && (
          <Field.Root invalid>
            <Field.ErrorText mt={2}>
              {serverErrors.autoGenerationEnabled || autoFormik.errors.autoGenerationEnabled}
            </Field.ErrorText>
          </Field.Root>
        )}
      </Box>

      {/* Enabled content */}
      {enabled && (
        <>
          {/* Mode cards (as in mockups) */}
          <Flex direction="column" gap={4}>
            <Field.Root>
              <Field.Label>Modus auswählen {requiredMark()}</Field.Label>
            </Field.Root>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              {modeCards.map(c => {
                const selected =
                  (c.key === 'CREATE_AND_SEND' && sendAfterAutoGeneration) ||
                  (c.key === 'CREATE_ONLY' && !sendAfterAutoGeneration)

                return (
                  <Card.Root
                    key={c.key}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      autoFormik.setFieldValue(
                        'sendAfterAutoGeneration',
                        c.key === 'CREATE_AND_SEND'
                      )
                      clearServerError('sendAfterAutoGeneration')
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        autoFormik.setFieldValue(
                          'sendAfterAutoGeneration',
                          c.key === 'CREATE_AND_SEND'
                        )
                        clearServerError('sendAfterAutoGeneration')
                      }
                    }}
                    cursor="pointer"
                    userSelect="none"
                    variant={selected ? 'subtle' : 'outline'}
                    colorPalette="blue"
                  >
                    <Card.Body>
                      <VStack>
                        {c.icon}
                        <Card.Title>{c.title}</Card.Title>
                        <Card.Description>{c.description}</Card.Description>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                )
              })}
            </SimpleGrid>
          </Flex>

          {/* Zeitpunkt festlegen */}
          <Flex direction="column" gap={4}>
            <FormLayout.Title>Zeitpunkt festlegen</FormLayout.Title>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
              <Field.Root
                invalid={!!(serverErrors.autoGenerationDay || autoFormik.errors.autoGenerationDay)}
              >
                <Field.Label>Tag des Monats {requiredMark()}</Field.Label>
                <SettingsInput
                  type="number"
                  {...autoFormik.getFieldProps('autoGenerationDay')}
                  onChange={e => {
                    autoFormik.handleChange(e)
                    clearServerError('autoGenerationDay')
                  }}
                />
                {(serverErrors.autoGenerationDay || autoFormik.errors.autoGenerationDay) && (
                  <Field.ErrorText>
                    {serverErrors.autoGenerationDay || autoFormik.errors.autoGenerationDay}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root
                invalid={
                  !!(serverErrors.autoGenerationTime || autoFormik.errors.autoGenerationTime)
                }
              >
                <Field.Label>Uhrzeit {requiredMark()}</Field.Label>
                <SettingsInput
                  type="time"
                  {...autoFormik.getFieldProps('autoGenerationTime')}
                  onChange={e => {
                    autoFormik.handleChange(e)
                    clearServerError('autoGenerationTime')
                  }}
                />
                {(serverErrors.autoGenerationTime || autoFormik.errors.autoGenerationTime) && (
                  <Field.ErrorText>
                    {serverErrors.autoGenerationTime || autoFormik.errors.autoGenerationTime}
                  </Field.ErrorText>
                )}
              </Field.Root>
            </SimpleGrid>
          </Flex>
        </>
      )}

      {/* ✅ Action bar (instead of the old buttons) */}
      <SettingsActionBar
        visible={(autoFormik.dirty && hasAutoChanged) || saving}
        onCancel={() => autoFormik.resetForm()}
        onSave={saveAuto}
        saving={saving}
        saveDisabled={!autoFormik.dirty || !autoFormik.isValid || !hasAutoChanged || saving}
        cancelDisabled={saving}
      />
    </FormLayout.Root>
  )
}
